import { useEffect, useState } from 'react';

import { ADMIN_API_BASE, apiClient } from '@infrastructure/api/client';
import { errorText } from '@infrastructure/api/errors';

interface Health {
  db: string;
  redis: string;
  replication: { lag_seconds: number } | null;
  celery_queue: number | null;
  disk: { total_bytes: number; free_bytes: number; free_percent: number } | null;
}

interface Standby {
  application_name: string;
  client_addr: string;
  state: string;
  sync_state: string;
  replay_lag_bytes: number;
  reply_age_seconds: number | null;
}

interface ReplicationDetail {
  role: 'primary' | 'standby' | 'unknown';
  standbys: Standby[];
  standby_state?: {
    lag_seconds: number | null;
    received_lsn: string;
    replay_lsn: string;
    replay_lag_bytes: number;
  };
  error?: string;
}

interface Alert {
  fingerprint: string;
  labels: Record<string, string>;
  annotations: Record<string, string>;
  starts_at: string | null;
  ends_at: string | null;
  status: string;
}

interface AlertsPayload {
  status: 'ok' | 'degraded';
  alerts: Alert[];
  message?: string;
}

interface AuditSummary {
  totals: { total_24h: number; total_7d: number };
  actions_24h: Record<string, number>;
  actions_7d: Record<string, number>;
  top_actors_24h: { actor: string; count: number }[];
}

type Tab = 'health' | 'replication' | 'alerts' | 'audit';

// An alert's state: firing ('active') is the bad one
function AlertPill({ value }: { value: string }) {
  const cls = value === 'active' ? 'bg-red-100 text-red-800'
    : value === 'suppressed' ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-700';
  return <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${cls}`}>{value === 'active' ? 'firing' : value}</span>;
}

function StatusPill({ value }: { value: string }) {
  const ok = value === 'ok' || value === 'streaming' || value === 'active';
  return (
    <span
      className={[
        'inline-block rounded px-2 py-0.5 text-xs font-medium',
        ok ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800',
      ].join(' ')}
    >
      {value}
    </span>
  );
}

export function SystemPage() {
  const [tab, setTab] = useState<Tab>('health');
  return (
    <section>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">System</h2>
        <nav className="flex gap-1 text-sm">
          {([
            ['health', 'Health'],
            ['replication', 'Replication'],
            ['alerts', 'Alerts'],
            ['audit', 'Audit'],
          ] as [Tab, string][]).map(([id, label]) => (
            <button
              key={id}
              type="button"
              onClick={() => setTab(id)}
              className={[
                'rounded px-3 py-1',
                tab === id ? 'bg-slate-900 text-white' : 'border bg-white',
              ].join(' ')}
            >
              {label}
            </button>
          ))}
        </nav>
      </div>
      {tab === 'health' && <HealthTab />}
      {tab === 'replication' && <ReplicationTab />}
      {tab === 'alerts' && <AlertsTab />}
      {tab === 'audit' && <AuditTab />}
    </section>
  );
}

// ---------------------------------------------------------------------------

function HealthTab() {
  const [data, setData] = useState<Health | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    apiClient
      .get(`${ADMIN_API_BASE}/system/health/`)
      .then((r) => setData(r.data))
      .catch((e) => setError(errorText(e)));
  }, [refreshKey]);

  if (error) return <div className="text-sm text-red-700">{error}</div>;
  if (!data) return <div className="text-sm text-slate-500">Loading…</div>;

  return (
    <div>
      <button
        type="button"
        onClick={() => setRefreshKey((k) => k + 1)}
        className="mb-3 rounded border px-3 py-1 text-sm"
      >
        Refresh
      </button>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Card title="Database" body={<StatusPill value={data.db} />} />
        <Card title="Redis" body={<StatusPill value={data.redis} />} />
        <Card
          title="Replication"
          body={
            data.replication
              ? `Standby — lag: ${data.replication.lag_seconds}s`
              : 'Primary (no recovery state)'
          }
        />
        <Card
          title="Celery queue"
          body={data.celery_queue !== null ? `${data.celery_queue} waiting` : 'Not measured'}
        />
        <Card
          title="Disk (/)"
          body={
            data.disk ? (
              <span className={data.disk.free_percent < 20 ? 'font-medium text-red-700' : ''}>
                {(data.disk.free_bytes / 1e9).toFixed(1)} GB free of {(data.disk.total_bytes / 1e9).toFixed(1)} GB
                {' '}({data.disk.free_percent}%)
              </span>
            ) : 'Not measured'
          }
        />
      </div>
    </div>
  );
}

function ReplicationTab() {
  const [data, setData] = useState<ReplicationDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiClient
      .get(`${ADMIN_API_BASE}/system/replication/`)
      .then((r) => setData(r.data))
      .catch((e) => setError(errorText(e)));
  }, []);

  if (error) return <div className="text-sm text-red-700">{error}</div>;
  if (!data) return <div className="text-sm text-slate-500">Loading…</div>;

  return (
    <div>
      <Card title="Role" body={<strong className="capitalize">{data.role}</strong>} />
      {data.error && <div className="mt-3 text-sm text-red-700">{data.error}</div>}

      {data.role === 'primary' && (
        <>
          <h3 className="mt-4 mb-2 text-sm font-semibold">Connected standbys</h3>
          {data.standbys.length === 0 ? (
            <p className="text-sm text-slate-500">No standbys connected.</p>
          ) : (
            <div className="overflow-x-auto rounded-lg border bg-white">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left">App</th>
                  <th className="px-3 py-2 text-left">Client addr</th>
                  <th className="px-3 py-2 text-left">State</th>
                  <th className="px-3 py-2 text-left">Sync</th>
                  <th className="px-3 py-2 text-right">Lag (bytes)</th>
                  <th className="px-3 py-2 text-right">Reply age (s)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {data.standbys.map((s) => (
                  <tr key={s.application_name + s.client_addr}>
                    <td className="px-3 py-2 font-mono text-xs">{s.application_name}</td>
                    <td className="px-3 py-2 font-mono text-xs">{s.client_addr}</td>
                    <td className="px-3 py-2"><StatusPill value={s.state} /></td>
                    <td className="px-3 py-2">{s.sync_state}</td>
                    <td className="px-3 py-2 text-right">{s.replay_lag_bytes.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right">{s.reply_age_seconds ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </>
      )}

      {data.role === 'standby' && data.standby_state && (
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Card title="Lag (seconds)" body={String(data.standby_state.lag_seconds ?? '—')} />
          <Card title="Lag (bytes)" body={data.standby_state.replay_lag_bytes.toLocaleString()} />
          <Card title="Received LSN" body={<code className="font-mono text-xs">{data.standby_state.received_lsn}</code>} />
          <Card title="Replay LSN" body={<code className="font-mono text-xs">{data.standby_state.replay_lsn}</code>} />
        </div>
      )}
    </div>
  );
}

function AlertsTab() {
  const [data, setData] = useState<AlertsPayload | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiClient
      .get(`${ADMIN_API_BASE}/system/alerts/`)
      .then((r) => setData(r.data))
      .catch((e) => setError(errorText(e)));
  }, []);

  if (error) return <div className="text-sm text-red-700">{error}</div>;
  if (!data) return <div className="text-sm text-slate-500">Loading…</div>;

  return (
    <div>
      {data.status === 'degraded' && (
        <div className="mb-3 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          ⚠ Alertmanager unreachable — showing degraded view. {data.message}
        </div>
      )}
      <p className="mb-2 text-sm text-slate-600">
        {data.alerts.length === 0
          ? 'No active alerts.'
          : `${data.alerts.length} active alert${data.alerts.length === 1 ? '' : 's'}.`}
      </p>
      {data.alerts.length > 0 && (
        <div className="space-y-2">
          {data.alerts.map((a) => (
            <div key={a.fingerprint} className="rounded border bg-white p-3 text-sm shadow-sm">
              <div className="flex items-center justify-between">
                <strong className="text-slate-900">
                  {a.labels.alertname || a.fingerprint.slice(0, 8)}
                </strong>
                <AlertPill value={a.status} />
              </div>
              {a.annotations.summary && (
                <p className="mt-1 text-slate-700">{a.annotations.summary}</p>
              )}
              {a.annotations.description && (
                <p className="mt-1 text-xs text-slate-500">{a.annotations.description}</p>
              )}
              <div className="mt-2 flex flex-wrap gap-1 text-xs">
                {Object.entries(a.labels).map(([k, v]) => (
                  <span key={k} className="rounded bg-slate-100 px-2 py-0.5">
                    {k}={v}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AuditTab() {
  const [data, setData] = useState<AuditSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiClient
      .get(`${ADMIN_API_BASE}/audit/summary/`)
      .then((r) => setData(r.data))
      .catch((e) => setError(errorText(e)));
  }, []);

  if (error) return <div className="text-sm text-red-700">{error}</div>;
  if (!data) return <div className="text-sm text-slate-500">Loading…</div>;

  return (
    <div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Card title="Audit events (24h)" body={<strong>{data.totals.total_24h}</strong>} />
        <Card title="Audit events (7d)" body={<strong>{data.totals.total_7d}</strong>} />
      </div>

      <h3 className="mt-4 mb-2 text-sm font-semibold">Top actors (24h)</h3>
      {data.top_actors_24h.length === 0 ? (
        <p className="text-sm text-slate-500">No audit activity in the last 24h.</p>
      ) : (
        <ul className="rounded border bg-white text-sm">
          {data.top_actors_24h.map((a) => (
            <li key={a.actor} className="flex items-center justify-between border-b px-3 py-1.5 last:border-b-0">
              <span>{a.actor}</span>
              <strong className="text-slate-700">{a.count}</strong>
            </li>
          ))}
        </ul>
      )}

      <h3 className="mt-4 mb-2 text-sm font-semibold">Actions (24h)</h3>
      {Object.keys(data.actions_24h).length === 0 ? (
        <p className="text-sm text-slate-500">No actions in the last 24h.</p>
      ) : (
        <ul className="rounded border bg-white text-sm">
          {Object.entries(data.actions_24h).map(([action, count]) => (
            <li key={action} className="flex items-center justify-between border-b px-3 py-1.5 last:border-b-0">
              <code className="font-mono text-xs">{action}</code>
              <strong className="text-slate-700">{count}</strong>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Card({ title, body }: { title: string; body: React.ReactNode }) {
  return (
    <div className="rounded-lg border bg-white p-4 shadow-sm">
      <div className="mb-1 text-xs uppercase text-slate-500">{title}</div>
      <div className="text-sm text-slate-800">{body}</div>
    </div>
  );
}
